import { t as globalT, type Translate } from "@redotech/redo-i18n";
import {
  getDropoffServiceLevel,
  hasPackedDropoff,
} from "@redotech/redo-return-core-common/dropoff/utils";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { VirtualServiceLevel } from "@redotech/redo-return-dropoff-provider-common/virtual-middle-mile";
import { COMMON } from "@redotech/redo-team-common/localization/language";
import { assertNever } from "@redotech/util/type";

export interface DropoffProviderCopy {
  title: string;
  subtitle: string;
  /** Replaces the generic drop-off method subtitle, which this drop-off contradicts. */
  methodSubtitle?: string;
  privacyPolicyText?: string;
  privacyPolicyLinkText?: string;
  privacyPolicyUrl?: string;
}

/** A drop-off location or QR code entry. */
interface DropoffEntry {
  provider: DropoffProviderType;
  providerMetadata?: unknown;
}

/**
 * Copy for a return's drop-offs: packing copy when any of them is packed, so a
 * mixed return never tells the shopper packaging is optional; otherwise the
 * first drop-off's provider copy.
 */
export function getDropoffsCopy(
  dropoffs: readonly DropoffEntry[] | undefined,
  t: Translate = globalT,
): DropoffProviderCopy | undefined {
  if (hasPackedDropoff(dropoffs)) {
    return getDropoffProviderCopy(
      VIRTUAL_PROVIDER,
      t,
      VirtualServiceLevel.UNVERIFIED,
    );
  }
  const first = dropoffs?.[0];
  return first
    ? getDropoffProviderCopy(first.provider, t, getDropoffServiceLevel(first))
    : undefined;
}

/**
 * Stock copy for a dropoff provider, `undefined` when the generic drop-off
 * text applies. A default the portal builder overrides, never an override.
 * Unverified Virtual drop-offs are packed by the shopper, so they get their
 * own copy instead of the generic "no packaging" text.
 */
export function getDropoffProviderCopy(
  provider: DropoffProviderType,
  t: Translate = globalT,
  serviceLevel?: VirtualServiceLevel,
): DropoffProviderCopy | undefined {
  switch (provider) {
    case BLUE_YONDER_PROVIDER:
      return {
        title: COMMON.ShipmentMethodName_BLUE_YONDER_TITLE,
        subtitle: COMMON.ShipmentMethodName_BLUE_YONDER_SUBTEXT,
        privacyPolicyText: t(
          "By selecting you are agreeing to our privacy policy.",
        ),
      };
    case VIRTUAL_PROVIDER: {
      if (serviceLevel !== VirtualServiceLevel.UNVERIFIED) {
        return undefined;
      }
      const subtitle = t(
        "Pack your items in a box or bag and drop them off at a nearby location. No printing required.",
      );
      return {
        title: t("Pack your items, then drop them off"),
        subtitle,
        methodSubtitle: subtitle,
      };
    }
    case RETURN_BEAR_PROVIDER:
      return undefined;
    case HAPPY_RETURNS_PROVIDER:
      return {
        title: t("Drop off at any {{provider}} location", {
          provider: "Happy Returns®",
        }),
        subtitle: t(
          "Take it to one of our nearby locations. No need for packaging or a shipping label.",
        ),
        privacyPolicyText: t(
          "By continuing, you acknowledge that you have had an opportunity to read",
        ),
        privacyPolicyLinkText: t("{{provider}}' Privacy Policy", {
          provider: "Happy Returns",
        }),
        privacyPolicyUrl: "https://happyreturns.com/privacy-policy",
      };
    default:
      assertNever(provider);
  }
}
